import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  tableCellClasses,
  IconButton,
  Container,
  Stack,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';

import FolderIcon from '@mui/icons-material/Folder';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useLocation, useNavigate } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import { useDispatch } from 'react-redux';
import CancelIcon from '@mui/icons-material/Cancel';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Page from '../../components/Page';
import Label from '../../components/Label';
import { getDataSourceValidate } from '../../store/Slice/DataSource/DataSourceSlice';
import useDataSourceSelector from '../../store/Slice/DataSource/DataSourceSelector';
import PageTitle from '../../components/PageHeading';
import BrudCrumbs from '../../components/BreadCrumbs';
import { setIsLoading } from '../../store/Slice/Loader/LoaderSlice';
import RevenueService from '../services/RevenueService';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { NOT_AVAILABLE } from '../Global/Constants';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: '#fff',
  borderBottom: '1px solid #f4f6f8',
  '&:last-child td, &:last-child th': { border: 0 }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 12,
    fontWeight: '700',
    padding: '16px 12px',
    color: '#637381',
    textTransform: 'uppercase',
    backgroundColor: '#f8fafc'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '14px 12px',
    borderBottom: '1px solid #f4f6f8'
  }
}));

const getLabelColor = (s) =>
  ({
    New: 'info',
    Contacted: 'info',
    ERROR: 'error',
    WARNING: 'warning',
    DND: 'warning',
    VALID: 'success',
    INVALID: 'error',
    Submitted: 'success'
  }[s] || 'warning');

const getCardTheme = (label) => {
  switch (label) {
    case 'No. of Records':
      return { iconBg: '#e0f2fe', iconColor: '#0284c7', borderColor: '#e2e8f0' };
    case 'Valid Records':
      return { iconBg: '#dcfce7', iconColor: '#16a34a', borderColor: '#bbf7d0' };
    case 'Invalid Records':
      return { iconBg: '#fee2e2', iconColor: '#dc2626', borderColor: '#fecaca' };
    case 'Phone - FPD':
      return { iconBg: '#fef3c7', iconColor: '#d97706', borderColor: '#fde68a' };
    default:
      return { iconBg: '#f1f5f9', iconColor: '#64748b', borderColor: '#e2e8f0' };
  }
};

export default function DataValidate() {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { DownloadDataValidateRecord } = RevenueService;

  const dispatch = useDispatch();
  const location = useLocation();
  const { rowId } = location.state || {};
  const navigate = useNavigate();

  const getViewDataSourceListApi = useCallback(() => {
    dispatch(getDataSourceValidate({ rowId }));
  }, [dispatch, rowId]);

  useEffect(() => {
    getViewDataSourceListApi();
  }, [getViewDataSourceListApi]);

  const { DataSourceValidateData } = useDataSourceSelector();
  const { LoginData } = useLoginSelector();

  const handleOpen = useCallback((e) => setAnchorEl(e.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const summaryCards = useMemo(
    () => [
      {
        icon: <FolderIcon fontSize="medium" />,
        value: DataSourceValidateData?.totalRecords ?? NOT_AVAILABLE,
        label: 'No. of Records'
      },
      {
        icon: <TrendingUpIcon fontSize="medium" />,
        value: DataSourceValidateData?.validRecords ?? NOT_AVAILABLE,
        label: 'Valid Records'
      },
      {
        icon: <WarningAmberIcon fontSize="medium" />,
        value: DataSourceValidateData?.invalidRecords ?? NOT_AVAILABLE,
        label: 'Invalid Records'
      },
      {
        icon: <AccessTimeIcon fontSize="medium" />,
        value: DataSourceValidateData?.phoneFpd ?? NOT_AVAILABLE,
        label: 'Phone - FPD'
      }
    ],
    [DataSourceValidateData]
  );

  const handleSelect = useCallback(
    async (type) => {
      const credential = {
        datasetId: rowId,
        type: type
      };
      try {
        const DownloadvalidateDataResponse = await DownloadDataValidateRecord(
          credential,
          LoginData?.jwt
        );

        const blob = await DownloadvalidateDataResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${type} Records.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('error', error);
      }
      handleClose();
    },
    [rowId, DownloadDataValidateRecord, LoginData?.jwt, handleClose]
  );

  return (
    <Page title="Partner Management System || Data Validate">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
            className="pageHeading"
          >
            <PageTitle info="Data Validate" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          {/* -------- SUMMARY METRIC CARDS -------- */}
          <Grid container spacing={3} mb={3}>
            {summaryCards?.map((card, i) => {
              const theme = getCardTheme(card.label);
              return (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      borderRadius: '16px',
                      border: `1px solid ${theme.borderColor}`,
                      bgcolor: '#fff',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.02)',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 18px rgba(0,0,0,0.06)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: theme.iconBg,
                        color: theme.iconColor
                      }}
                    >
                      {card?.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e293b' }}>
                        {card?.value}
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 500 }}>
                        {card?.label}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {/* -------- TABLE CARD -------- */}
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: '20px',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              boxShadow: '0 4px 14px rgba(0,0,0,0.03)',
              mb: 3
            }}
          >
            <Box
              sx={{
                background: 'linear-gradient(135deg, #13519d 0%, #1565c0 100%)',
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#fff' }}>
                Data Validation Results
              </Typography>

              <Tooltip title="Download Records" placement="top" arrow>
                <IconButton
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
                  }}
                  size="small"
                  onClick={handleOpen}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem onClick={() => handleSelect('VALID')}>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Valid Report" />
                </MenuItem>

                <MenuItem onClick={() => handleSelect('INVALID')}>
                  <ListItemIcon>
                    <CancelIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Invalid Report" />
                </MenuItem>

                <MenuItem onClick={() => handleSelect('ALL')}>
                  <ListItemIcon>
                    <DescriptionIcon color="warning" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="All Reports" />
                </MenuItem>
              </Menu>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Field Name</StyledTableCell>
                    <StyledTableCell>Status</StyledTableCell>
                    <StyledTableCell>Message</StyledTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {DataSourceValidateData?.validationResults?.map((r) => (
                    <StyledTableRow key={r.fieldName}>
                      <StyledTableCell sx={{ fontWeight: 600, color: '#13519d' }}>
                        {r.fieldName}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Label variant="ghost" color={getLabelColor(r.status)}>
                          {r.status}
                        </Label>
                      </StyledTableCell>
                      <StyledTableCell sx={{ color: '#637381' }}>{r.message}</StyledTableCell>
                    </StyledTableRow>
                  ))}
                  {(!DataSourceValidateData?.validationResults ||
                    DataSourceValidateData.validationResults.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3, color: '#94a3b8' }}>
                        No validation records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box textAlign="right">
            <Button
              type="button"
              variant="contained"
              onClick={() => {
                dispatch(setIsLoading({ loading: true }));
                setTimeout(() => {
                  dispatch(setIsLoading({ loading: false }));
                  navigate(`/data/check-bureau`);
                }, 1000);
              }}
              sx={{
                bgcolor: '#13519d',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                px: 4,
                py: 1.2,
                boxShadow: '0 4px 14px rgba(19,81,157,0.25)',
                '&:hover': { bgcolor: '#0d3b75' }
              }}
            >
              Proceed to Check Bureau
            </Button>
          </Box>
        </Box>
      </Container>
    </Page>
  );
}
